// Таблица «месяц и объект» одного юридического лица: те же колонки и тот же порядок, что в листе
// заказчика «Расчёт Шаврин». Каждая строка блока — одна ведомость человека, внизу сводка «Всего».
//
// ## Почему таблица, а где она не помещается, карточки
//
// Финансовый директор проверяет расчёт глазом по столбцу: сколько выдали авансом за всё время,
// сколько удержано за форму. В образце это шесть блоков подряд и сводка под ними; карточки с
// одиннадцатью полями каждая растянули бы то же самое на три экрана, и столбец пропал бы. Поэтому
// там, где таблица помещается в окно целиком, она и стоит.
//
// Таблица, которая не помещается, хуже карточек. На планшете в портрете (744 точки) она была
// шире окна на 450 точек: «Официально», «Форма», «Всего к выплате», «Выплачено» и «Осталось» уходили
// за край без единой подсказки о прокрутке, а пояснения под строками обрывались на полуслове. Тому
// человеку, который открыл расчёт ради итога блока, как раз эти колонки и нужны. Поэтому выбор
// между таблицей и карточками сделан в `БлокиРасчёта`: узкое окно и таблица, которая не влезла
// при замере, получают карточки (`КарточкиБлоков`), а за краем не остаётся ничего.
//
// ## Какие колонки видны
//
// Аванс, билеты, официально и форма стоят всегда, как в образце: их ноль — тоже ответ. Обучение,
// штраф, премия и «прочие удержания» появляются, только если у кого-то из блоков они не нулевые:
// иначе три пустые колонки съедают ширину, а ряд от этого не перестаёт сходиться (ноль слагаемого
// ничего не меняет).
//
// ## Ноль, неизвестное и пустое выглядят по-разному (правило 7)
//
// Ноль по расчёту — «0» приглушённо. Расчёт по человеку не проводился — прочерк: там нули были бы
// выдумкой. Часы не проставлены — прочерк и подпись «табель не сдан»: «не выходил» и «табель не
// сдан» разные факты, и по ним решают, платить ли.

import { Link } from 'react-router';

import type { PayslipPeriodBlock, PayslipPeriodTotals } from '@/shared/api/endpoints/payslip';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { formatPeriodTitle } from '@/shared/time/period';
import * as Badge from '@/shared/ui/badge';
import { cn } from '@/utils/cn';

import {
  адресВедомости,
  итогНеизвестен,
  непосчитанноеВБлоках,
  сЗаглавной,
  часыТекстом,
} from './тексты';

export interface Колонка {
  ключ: string;
  заголовок: string;
  /** Подсказка к заголовку: что за колонка, когда название одно не объясняет. */
  пояснение?: string;
  значение: (блок: PayslipPeriodBlock) => number;
  итог: (итог: PayslipPeriodTotals) => number;
  /** Колонка показывается, только если хоть где-то не ноль. */
  необязательная?: boolean;
  /** Итог строки: выделена жирным, она и есть ответ блока. */
  главная?: boolean;
  /** Колонки, в которых прочерк не нужен: выплаты знают и не рассчитанные ведомости. */
  всегдаЧисло?: boolean;
}

const КОЛОНКИ: readonly Колонка[] = [
  {
    ключ: 'accrued',
    заголовок: 'Начислено',
    пояснение: 'по часам и тарифу, без премии',
    значение: (блок) => блок.accrued,
    итог: (итог) => итог.accrued,
  },
  {
    ключ: 'advance',
    заголовок: 'Аванс',
    значение: (блок) => блок.advance,
    итог: (итог) => итог.advance,
  },
  {
    ключ: 'tickets',
    заголовок: 'Билеты',
    значение: (блок) => блок.tickets,
    итог: (итог) => итог.tickets,
  },
  {
    ключ: 'official_total',
    заголовок: 'Официально',
    пояснение: 'выплачено по официальной ведомости и остаток по ней',
    значение: (блок) => блок.official_total,
    итог: (итог) => итог.official_total,
  },
  {
    ключ: 'training',
    заголовок: 'Обучение',
    значение: (блок) => блок.training,
    итог: (итог) => итог.training,
    необязательная: true,
  },
  {
    ключ: 'fine',
    заголовок: 'Штраф',
    значение: (блок) => блок.fine,
    итог: (итог) => итог.fine,
    необязательная: true,
  },
  {
    ключ: 'uniform',
    заголовок: 'Форма',
    значение: (блок) => блок.uniform,
    итог: (итог) => итог.uniform,
  },
  {
    ключ: 'other_deductions',
    заголовок: 'Прочие удержания',
    пояснение: 'удостоверение, кредит, квартиры, зарплата на объектах',
    значение: (блок) => блок.other_deductions,
    итог: (итог) => итог.other_deductions,
    необязательная: true,
  },
  {
    ключ: 'bonus',
    заголовок: 'Премия',
    пояснение: 'прибавляется к начисленному',
    значение: (блок) => блок.bonus,
    итог: (итог) => итог.bonus,
    необязательная: true,
  },
  {
    ключ: 'total_payout',
    заголовок: 'Всего к выплате',
    значение: (блок) => блок.total_payout,
    итог: (итог) => итог.to_issue,
    главная: true,
  },
  {
    ключ: 'paid',
    заголовок: 'Выплачено',
    значение: (блок) => блок.paid,
    итог: (итог) => итог.paid,
    всегдаЧисло: true,
  },
  {
    ключ: 'remaining',
    заголовок: 'Осталось',
    // Не «всегда число», в отличие от «Выплачено»: выплаты факт и известны у любой ведомости, а
    // остаток считается от «Всего к выплате», которого у ведомости в работе без расчёта нет. «0»
    // здесь читался бы как «платить нечего».
    пояснение: 'осталось выплатить',
    значение: (блок) => блок.remaining,
    итог: (итог) => итог.remaining,
  },
];

/** Колонки, которые нужны этой группе блоков. Порядок образца сохраняется. */
export function видимыеКолонки(блоки: readonly PayslipPeriodBlock[]): readonly Колонка[] {
  return КОЛОНКИ.filter(
    (колонка) =>
      колонка.необязательная !== true || блоки.some((блок) => колонка.значение(блок) !== 0),
  );
}

/**
 * Известно ли число этой колонки у этого блока: расчёт был, либо ведомость закрыта, либо колонка
 * знает и такие блоки.
 *
 * Закрытая ведомость без расчёта (месяц перенесён из книги заказчика) числами во ВСЕХ колонках:
 * деньги в ней записаны при переносе и входят в итог внизу, а прочерки в строке при «Выплачено
 * 32 298» читались как «начислено неизвестно» (замечание независимой проверки 01.10.2026). Прочерк
 * остаётся у ведомости в работе: расчёта по ней не было, и нули были бы выдумкой.
 */
export function числоИзвестно(блок: PayslipPeriodBlock, колонка: Колонка): boolean {
  return блок.calculated || блок.is_final || колонка.всегдаЧисло === true;
}

export function Деньги({
  копейки,
  известно = true,
  главная = false,
}: {
  копейки: number;
  известно?: boolean;
  главная?: boolean;
}) {
  if (!известно) {
    return <span className='text-text-sub-600'>—</span>;
  }
  return (
    <span
      className={cn(
        копейки === 0 ? 'text-text-sub-600' : 'text-text-strong-950',
        главная && копейки !== 0 && 'text-label-sm',
      )}
    >
      {formatKopecks(копейки)}
    </span>
  );
}

/** Состояние ведомости словом сервера и её номер: «Утверждена № 2026-03/1». */
export function СостояниеВедомости({ блок }: { блок: PayslipPeriodBlock }) {
  return (
    <span className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
      <Badge.Root variant='lighter' size='medium' color={блок.is_final ? 'green' : 'gray'}>
        {блок.status_title}
      </Badge.Root>
      {блок.number !== null && блок.number !== undefined && (
        <span className='text-paragraph-xs text-text-sub-600'>№ {блок.number}</span>
      )}
    </span>
  );
}

function Месяц({ блок }: { блок: PayslipPeriodBlock }) {
  const месяц = { year: блок.period_year, month: блок.period_month };
  return (
    <div className='flex min-w-36 flex-col gap-0.5'>
      <Link
        to={адресВедомости(блок.period_year, блок.period_month, блок.payroll_id)}
        className='цель-тача relative w-fit text-label-sm text-text-strong-950 underline-offset-4 hover:underline'
      >
        {formatPeriodTitle(месяц)}
      </Link>
      <span className='max-w-40 whitespace-normal break-words text-paragraph-xs text-text-sub-600'>
        {блок.site_name ?? 'Аппарат управления'}
      </span>
      <СостояниеВедомости блок={блок} />
    </div>
  );
}

/** Больше часов в месяц не бывает: 31 день по 24. Норма выше этого не норма человека. */
const ЧАСОВ_В_МЕСЯЦЕ = 744;

/**
 * Часы блока для чтения: число с нормой, либо прочерк с причиной. Одно и для таблицы, и для карточки.
 *
 * Норма больше 744 часов не показывается: «276 из 2604» читается как «отработал десятую часть», а
 * это норма ОБЪЕКТА, попавшая в карточку человека при переносе книги (на боевых данных 01.10.2026
 * так выглядят перенесённые майские месяцы). Правило проекта то же: норму больше 744 в человека не
 * писать (`CLAUDE.md`, «Факты, которые путают»).
 */
export function частиЧасов(блок: PayslipPeriodBlock): { часы: string; норма: string | null } {
  if (блок.hours === null || блок.hours === undefined) {
    return { часы: '—', норма: null };
  }
  const норма = блок.norm_hours;
  const правдоподобна = норма !== null && норма !== undefined && Number(норма) <= ЧАСОВ_В_МЕСЯЦЕ;
  return {
    часы: часыТекстом(блок.hours),
    норма: правдоподобна ? `из ${часыТекстом(норма)}` : null,
  };
}

function Часы({ блок }: { блок: PayslipPeriodBlock }) {
  if (блок.hours === null || блок.hours === undefined) {
    return (
      <div className='flex flex-col items-end'>
        <span className='text-text-sub-600'>—</span>
        <span className='text-paragraph-xs text-text-sub-600'>табель не сдан</span>
      </div>
    );
  }
  const { часы, норма } = частиЧасов(блок);
  return (
    <div className='flex flex-col items-end'>
      <span className='text-text-strong-950'>{часы}</span>
      {норма !== null && <span className='text-paragraph-xs text-text-sub-600'>{норма}</span>}
    </div>
  );
}

/** Тариф блока: число, пояснение «сумма ÷ норма» и слово «несколько» вместо одного числа. */
export function частиТарифа(блок: PayslipPeriodBlock): { тариф: string; расчёт: string | null } {
  if (блок.rate === null || блок.rate === undefined) {
    return { тариф: блок.tariff_lines.length > 1 ? 'несколько' : '—', расчёт: null };
  }
  return {
    тариф: formatKopecks(блок.rate * 100),
    расчёт:
      блок.period_amount !== null &&
      блок.period_amount !== undefined &&
      блок.norm_hours !== null &&
      блок.norm_hours !== undefined
        ? `${formatKopecks(блок.period_amount)} ÷ ${часыТекстом(блок.norm_hours)}`
        : null,
  };
}

function Тариф({ блок }: { блок: PayslipPeriodBlock }) {
  const { тариф, расчёт } = частиТарифа(блок);
  if (блок.rate === null || блок.rate === undefined) {
    return <span className='text-text-sub-600'>{тариф}</span>;
  }
  return (
    <div className='flex flex-col items-end'>
      <span className='text-text-strong-950'>{тариф}</span>
      {расчёт !== null && <span className='text-paragraph-xs text-text-sub-600'>{расчёт}</span>}
    </div>
  );
}

/**
 * Место блока для сверки с пробелами документа: объект, год и месяц одной строкой. Пробел «табель не
 * сдан» и блок за тот же месяц и объект — один и тот же факт, сказанный дважды.
 */
export function ключМеста(объект: string | null | undefined, год: number, месяц: number): string {
  return `${объект ?? ''}|${год}|${месяц}`;
}

/** Причина сервера «часы ещё не проставлены»: её же называет пробел «табель не сдан». */
const ПРИЧИНА_ЧАСОВ = /^часы за месяц ещё не проставлены/i;

/** Причина сервера у ведомости, расчёт по которой просто не проводился (перенесённой из книги тоже). */
const ПРИЧИНА_НЕ_СЧИТАЛСЯ = /^расчёт ещё не проводился/i;

/**
 * Замечания к блоку: почему деньги в нём нельзя принимать как есть. Строками под блоком.
 *
 * `часыУжеНазваны` — месяц и объект, по которым над таблицей уже стоит строка «табель не сдан».
 * Для них «часы за месяц ещё не проставлены» не повторяется: это тот же факт, и до 01.10.2026 он
 * стоял шесть раз подряд по строке на каждый такой месяц. Остальные причины («расчёт ещё не
 * проводился», «не заполнен тариф») остаются: пробел про них молчит.
 */
export function замечанияБлока(
  блок: PayslipPeriodBlock,
  часыУжеНазваны: ReadonlySet<string> = new Set(),
): string[] {
  const строки: string[] = [];
  if (блок.not_counted !== null && блок.not_counted !== undefined) {
    const повторПробела =
      ПРИЧИНА_ЧАСОВ.test(блок.not_counted) &&
      часыУжеНазваны.has(ключМеста(блок.site_id, блок.period_year, блок.period_month));
    // Закрытая из книги: «расчёт ещё не проводился» про неё правда, но «цифрам нельзя верить» нет:
    // цифр в строке нет (прочерки), а что месяц перенесён выплаченным, говорит строка про выплату ниже.
    const изКниги = блок.is_final && ПРИЧИНА_НЕ_СЧИТАЛСЯ.test(блок.not_counted);
    if (!повторПробела && !изКниги) {
      строки.push(`Цифрам этой ведомости пока нельзя верить. ${сЗаглавной(блок.not_counted)}.`);
    }
  }
  if (блок.adjustment_note !== null && блок.adjustment_note !== undefined) {
    строки.push(`${блок.adjustment_note}.`);
  }
  if (блок.paid_note !== null && блок.paid_note !== undefined) {
    строки.push(`Выплата: ${блок.paid_note}.`);
  }
  if (блок.overpaid > 0) {
    строки.push(
      `Выплачено на ${formatRubles(блок.overpaid)} больше, чем положено. Система сама ничего не удерживает.`,
    );
  }
  return строки;
}

export function ТаблицаБлоков({
  блоки,
  итог,
  подпись,
  часыУжеНазваны,
}: {
  блоки: readonly PayslipPeriodBlock[];
  итог: PayslipPeriodTotals;
  /** Для чтения с экрана: какому юрлицу принадлежит таблица. */
  подпись: string;
  /** Месяцы и объекты, по которым над таблицей уже сказано «табель не сдан». */
  часыУжеНазваны?: ReadonlySet<string>;
}) {
  const колонки = видимыеКолонки(блоки);
  const ширина = колонки.length + 3;
  // Ни по одной ведомости расчёта не было: «Всего» не нули, а неизвестно (как и строки над ним).
  const неизвестен = итогНеизвестен(непосчитанноеВБлоках(блоки), итог);
  // Ни минимальной ширины, ни прокрутки у самой таблицы нет: заголовки колонок переносятся по
  // словам, числа не рвутся, а если и так не помещается, `БлокиРасчёта` рисует карточки. Таблица,
  // которую надо листать вбок, прятала «Всего к выплате» за краем окна.
  return (
    <table className='w-full border-collapse text-paragraph-sm'>
      <caption className='sr-only'>{подпись}</caption>
      <thead>
        <tr className='border-b border-stroke-soft-200 text-paragraph-xs text-text-sub-600'>
          <th scope='col' className='px-3 py-2 text-left align-bottom font-normal'>
            Месяц и объект
          </th>
          <th scope='col' className='px-2 py-2 text-right align-bottom font-normal'>
            Часы
          </th>
          <th scope='col' className='px-2 py-2 text-right align-bottom font-normal'>
            Тариф
          </th>
          {колонки.map((колонка) => (
            <th
              key={колонка.ключ}
              scope='col'
              title={колонка.пояснение}
              className={cn(
                'px-2 py-2 text-right align-bottom font-normal',
                колонка.главная && 'text-text-strong-950',
              )}
            >
              {колонка.заголовок}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {блоки.map((блок) => (
          <СтрокиБлока
            key={блок.payroll_id}
            блок={блок}
            колонки={колонки}
            ширина={ширина}
            примечания={замечанияБлока(блок, часыУжеНазваны)}
          />
        ))}
      </tbody>
      <tfoot>
        <tr className='border-t border-stroke-soft-200 bg-bg-weak-50 text-paragraph-sm'>
          <th scope='row' className='px-3 py-2.5 text-left text-label-sm text-text-strong-950'>
            Всего
          </th>
          <td className='px-2 py-2.5 text-right tabular-nums text-text-strong-950'>
            {часыТекстом(итог.hours)}
          </td>
          <td />
          {колонки.map((колонка) => (
            <td
              key={колонка.ключ}
              className='whitespace-nowrap px-2 py-2.5 text-right tabular-nums'
            >
              <Деньги
                копейки={колонка.итог(итог)}
                известно={!неизвестен || колонка.всегдаЧисло === true}
                главная={колонка.главная === true}
              />
            </td>
          ))}
        </tr>
      </tfoot>
    </table>
  );
}

function СтрокиБлока({
  блок,
  колонки,
  ширина,
  примечания,
}: {
  блок: PayslipPeriodBlock;
  колонки: readonly Колонка[];
  ширина: number;
  примечания: readonly string[];
}) {
  return (
    <>
      <tr className={cn('align-top', примечания.length === 0 && 'border-b border-stroke-soft-200')}>
        <td className='px-3 py-2.5'>
          <Месяц блок={блок} />
        </td>
        <td className='whitespace-nowrap px-2 py-2.5 text-right tabular-nums'>
          <Часы блок={блок} />
        </td>
        <td className='whitespace-nowrap px-2 py-2.5 text-right tabular-nums'>
          <Тариф блок={блок} />
        </td>
        {колонки.map((колонка) => (
          <td key={колонка.ключ} className='whitespace-nowrap px-2 py-2.5 text-right tabular-nums'>
            <Деньги
              копейки={колонка.значение(блок)}
              известно={числоИзвестно(блок, колонка)}
              главная={колонка.главная === true}
            />
          </td>
        ))}
      </tr>
      {примечания.length > 0 && (
        <tr className='border-b border-stroke-soft-200'>
          {/* Замечание на всю ширину таблицы и переносится по словам: в прежней таблице с боковой
              прокруткой оно тянулось строкой шире окна и обрывалось на полуслове. */}
          <td colSpan={ширина} className='px-3 pb-2.5'>
            <ul className='flex flex-col gap-0.5'>
              {примечания.map((строка) => (
                <li key={строка} className='text-paragraph-xs text-text-sub-600'>
                  {строка}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}
